/**
 * React bindings of the state stores (W1-09 state-store; contract src/state/types.ts, ARCHITECTURE.md §5.6).
 * The only state/ file that imports react.
 *
 *  - `DocStoreProvider` / `useDocStore`: the project's DocStore through context (`useOptionalDocStore` outside a
 *    project, e.g. the Library, returns null instead of throwing).
 *  - `useDoc(selector, topics, equal?)`: `useSyncExternalStore` keyed on version topics. The selector re-runs only
 *    when one of `topics` changed (or the selector itself changed between renders); the component re-renders only
 *    when the selected value is not `equal` (default `Object.is`) to the previous one. Records are immutable, so
 *    selecting a record or an index result and comparing by identity is enough.
 *  - `useHistory(selector, equal?)`: the undo history (History panel, undo/redo buttons).
 *  - `useExternalSelector`: the memoised selector hook behind them and `useEditor` (state/editorStore.ts).
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { Unsubscribe } from '../core/util/types';
import type { ChartDoc, DocIndexes } from '../core/model/types';
import type { DocStore, HistoryView, UseDoc, VersionTopic } from './types';
import { hasVersionSubscriptions } from './docStore';

const DocStoreContext = createContext<DocStore | null>(null);
DocStoreContext.displayName = 'DocStore';

export interface DocStoreProviderProps {
  readonly store: DocStore;
  readonly children?: ReactNode;
}

export function DocStoreProvider(props: DocStoreProviderProps): ReactNode {
  return createElement(DocStoreContext.Provider, { value: props.store }, props.children);
}

/** The DocStore of the open project; throws outside a `DocStoreProvider`. */
export const useDocStore: () => DocStore = () => {
  const store = useContext(DocStoreContext);
  if (store === null) throw new Error('useDocStore: no DocStoreProvider above this component (is a project open?)');
  return store;
};

/** The DocStore of the open project, or null outside a `DocStoreProvider`. */
export function useOptionalDocStore(): DocStore | null {
  return useContext(DocStoreContext);
}

interface Memo<S, T> {
  readonly source: unknown;
  readonly snapshot: S;
  readonly selector: unknown;
  readonly value: T;
}

/**
 * Subscribes to an external source and returns `selector(getSnapshot())`, memoised: the selector re-runs when the
 * snapshot or the selector changes, and an `equal` result keeps the previous value (so consumers see a stable
 * identity). `source` identifies the store (a different store never reuses a memo).
 */
export function useExternalSelector<S, T>(
  source: unknown,
  subscribe: (onChange: () => void) => Unsubscribe,
  getSnapshot: () => S,
  selector: (snapshot: S) => T,
  equal: (a: T, b: T) => boolean = Object.is,
): T {
  const memo = useRef<Memo<S, T> | null>(null);
  const getSelection = useCallback((): T => {
    const snapshot = getSnapshot();
    const prev = memo.current;
    const same = prev !== null && prev.source === source;
    if (same && Object.is(prev.snapshot, snapshot) && prev.selector === selector) return prev.value;
    const next = selector(snapshot);
    const value = same && equal(prev.value, next) ? prev.value : next;
    memo.current = { source, snapshot, selector, value };
    return value;
  }, [source, getSnapshot, selector, equal]);
  return useSyncExternalStore(subscribe, getSelection, getSelection);
}

/** Topics as a stable array (callers usually pass an inline literal). */
function useStableTopics(topics: readonly VersionTopic[]): readonly VersionTopic[] {
  const key = topics.join('\u0000');
  return useMemo(() => (key === '' ? [] : (key.split('\u0000') as VersionTopic[])), [key]);
}

export const useDoc: UseDoc = <T>(
  selector: (doc: ChartDoc, idx: DocIndexes) => T,
  topics: readonly VersionTopic[],
  equal?: (a: T, b: T) => boolean,
): T => {
  const store = useDocStore();
  const stable = useStableTopics(topics);
  // Document swaps seen by the fallback path (a minimal fake with only `version` / `subscribe`): its `version` counters
  // may not include loads, so a swap must still re-run the selector.
  const swaps = useRef(0);
  const subscribe = useCallback(
    (onChange: () => void): Unsubscribe =>
      hasVersionSubscriptions(store)
        ? store.subscribeVersions(stable, onChange)
        : store.subscribe((cs) => {
            if (cs.source === 'load') swaps.current++;
            onChange();
          }),
    [store, stable],
  );
  const getStamp = useCallback((): number => {
    if (hasVersionSubscriptions(store)) return store.stamp(stable);
    let n = swaps.current;
    for (const t of stable) n += store.version(t);
    return n;
  }, [store, stable]);
  const select = useCallback((): T => selector(store.doc, store.idx), [store, selector]);
  // The memo is keyed on the store AND the topics: two topic lists can have equal stamps, so a change of topics must
  // re-run the selector even when the selector function is the same.
  const source = useMemo(() => ({ store, topics: stable }), [store, stable]);
  return useExternalSelector(source, subscribe, getStamp, select, equal);
};

/** Change counter of a history view (the DocStore's view has one; other views count notifications). */
function historyVersion(h: HistoryView): number | null {
  const v = (h as Partial<{ readonly version: number }>).version;
  return typeof v === 'number' ? v : null;
}

/**
 * Selects from the open project's undo history and re-renders when the selection changes, e.g.
 * `useHistory((h) => h.canUndo)` or `useHistory((h) => h.entries())` (entries are cached per change).
 */
export function useHistory<T>(selector: (h: HistoryView) => T, equal?: (a: T, b: T) => boolean): T {
  const store = useDocStore();
  const fallback = useRef(0);
  const subscribe = useCallback(
    (onChange: () => void): Unsubscribe =>
      store.history.subscribe(() => {
        fallback.current++;
        onChange();
      }),
    [store],
  );
  const getVersion = useCallback((): number => historyVersion(store.history) ?? fallback.current, [store]);
  const select = useCallback((): T => selector(store.history), [store, selector]);
  return useExternalSelector(store, subscribe, getVersion, select, equal);
}
